import { IonModal, IonContent, IonIcon, IonHeader, IonFooter } from "@ionic/react";
import { chevronBack, close as closeIcon, returnDownForward } from "ionicons/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { useDispatch, useSelector } from "react-redux";
import {
  closeCommentsSheet,
  clearCommentFocus,
} from "../../redux/reducer/commentsSheetSlice";
import { usePostComments } from "../../hooks/usePostComments";
import { InsertComment } from "../InsertComment";
import SheetCommentList from "./SheetCommentList";
import { arePostCommentsEnabled } from "../../utils/postComments";
import "./CommentsSheet.css";

const getVisualViewportKeyboardHeight = () => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;

  const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
  return Math.max(0, Math.round(overlap));
};

const CommentsSheet = () => {
  const dispatch = useDispatch();
  const contentRef = useRef(null);
  const footerRef = useRef(null);
  const modalRef = useRef(null);
  const nonceRef = useRef(0);
  const dragRef = useRef({ active: false, startY: 0, dy: 0 });

  const { isOpen, postGroup, focusCommentId } = useSelector(
    (s) => s.commentsSheet
  );

  const postId = postGroup?.id;
  const commentsEnabled = arePostCommentsEnabled(postGroup);
  // Latest list state for the focus poll further down, which must be able to
  // pull in another page without re-running every time the list changes.
  const listRef = useRef(null);
  const list = usePostComments(
    isOpen && commentsEnabled ? postId : null,
    focusCommentId
  );
  listRef.current = list;

  // Flat replies (one level): replyTarget = { elementId (where the reply is
  // posted — always the top-level comment), parentId (same), name (who we're
  // replying to, for the banner) }. lastReply is broadcast to the matching
  // top-level comment so the new reply appears inline.
  const [replyTarget, setReplyTarget] = useState(null);
  const [lastReply, setLastReply] = useState(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  // Android only: the WebView resizes natively for the keyboard (see the iOS
  // effect below for why we don't lift the sheet on Android). A fixed-% sheet
  // then gets squished into the smaller viewport, so while the keyboard is open
  // we expand the sheet to full height; the peek height returns on close.
  const [androidKbOpen, setAndroidKbOpen] = useState(false);
  // When true, top-level comments expand their replies so a deep-linked reply
  // renders and can be scrolled to.
  const [expandForFocus, setExpandForFocus] = useState(false);

  const close = useCallback(() => dispatch(closeCommentsSheet()), [dispatch]);

  useEffect(() => {
    if (isOpen && postGroup?.post && !commentsEnabled) {
      close();
    }
  }, [isOpen, postGroup, commentsEnabled, close]);

  // Replying to a comment OR a reply always targets the TOP-LEVEL parent, so
  // the thread stays one level deep.
  const handleReplyTo = useCallback((displayComment, parentComment) => {
    const parent = parentComment || displayComment;
    // Reply-to-reply = the tapped comment is not the top-level parent. Only then
    // do we persist "In risposta a [nome]" (the flat one-level thread needs the
    // disambiguation; a direct reply already sits under its comment).
    const isReplyToReply = String(parent.id) !== String(displayComment.id);
    setReplyTarget({
      elementId: parent.id,
      parentId: parent.id,
      name: displayComment?.created_by?.subscriber_full_name || "commento",
      replyToName: isReplyToReply
        ? displayComment?.created_by?.subscriber_full_name || null
        : null,
    });
  }, []);

  // Reset the reply state whenever the sheet (re)opens or the post changes.
  useEffect(() => {
    setReplyTarget(null);
    setLastReply(null);
    setExpandForFocus(false);
  }, [isOpen, postId]);

  // A reply was posted — show it inline under its parent comment and bump the
  // parent's reply count, then return the composer to the post.
  const handleReplyAdded = useCallback(
    (reply) => {
      const parentId = replyTarget?.parentId;
      if (parentId == null) return;
      nonceRef.current += 1;
      setLastReply({ parentId, reply, nonce: nonceRef.current });
      list.setComments((prev) =>
        prev.map((c) =>
          String(c.id) === String(parentId)
            ? {
                ...c,
                replies_count: (c.replies_count ?? 0) + 1,
                last_reply: reply,
              }
            : c
        )
      );
      setReplyTarget(null);
    },
    [replyTarget, list]
  );

  // Keyboard handling for the sheet. The app globally uses resize="native",
  // which shrinks the WebView when the keyboard opens. This custom bottom sheet
  // needs to keep its own height, so while open it switches to resize="none"
  // and translates above the keyboard. Both will/did events are used because
  // Android can report the final keyboard height only on the did event.
  useEffect(() => {
    // iOS ONLY. On iOS the WebView does not resize for the keyboard, so we
    // switch to resize=none and manually lift the sheet above it. Android uses
    // the app-wide "native" resize (the WebView already shrinks to sit above the
    // keyboard); applying the manual lift there stacks on top of that native
    // resize, over-lifting the sheet and exposing the feed (bottom tab bar +
    // FAB) behind it — so leave Android to native resize (keyboardOffset stays 0).
    if (!isOpen || Capacitor.getPlatform() !== "ios") return undefined;

    const listeners = [];
    let cancelled = false;

    const setOffset = (px) => {
      const el = modalRef.current;
      el?.style.setProperty("--cs-drag-anim", "0.25s");
      setKeyboardOffset(Math.max(0, Math.round(Number(px) || 0)));
    };
    const keyboardHeightFrom = (info) =>
      Math.max(info?.keyboardHeight || 0, getVisualViewportKeyboardHeight());
    const shiftForKeyboard = (info) => {
      const height = keyboardHeightFrom(info);
      setOffset(height);
    };
    const resetShift = () => setOffset(0);
    const onViewportChange = () => {
      const height = getVisualViewportKeyboardHeight();
      if (height > 0) setOffset(height);
    };

    (async () => {
      try {
        await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      } catch {
        /* ignore */
      }
      let nextListeners = [];
      try {
        nextListeners = await Promise.all([
          Keyboard.addListener("keyboardWillShow", shiftForKeyboard),
          Keyboard.addListener("keyboardDidShow", shiftForKeyboard),
          Keyboard.addListener("keyboardWillHide", resetShift),
          Keyboard.addListener("keyboardDidHide", resetShift),
        ]);
      } catch {
        nextListeners.forEach((listener) => listener?.remove?.());
        return;
      }

      if (cancelled) {
        nextListeners.forEach((listener) => listener?.remove?.());
        return;
      }

      listeners.push(...nextListeners);
    })();

    window.visualViewport?.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("scroll", onViewportChange);

    return () => {
      cancelled = true;
      listeners.forEach((listener) => listener?.remove?.());
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("scroll", onViewportChange);
      resetShift();
      // Restore the app-wide default so other screens keep native resize.
      Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
    };
  }, [isOpen]);

  // Android only: track keyboard open/close (native resize is left as-is) so we
  // can expand the sheet to full height while typing — no manual lift, so no
  // feed bleed-through, and the comment list isn't squished into a small sheet.
  useEffect(() => {
    if (!isOpen || Capacitor.getPlatform() !== "android") return undefined;

    let subs = [];
    let cancelled = false;

    (async () => {
      try {
        const next = await Promise.all([
          Keyboard.addListener("keyboardDidShow", () => setAndroidKbOpen(true)),
          Keyboard.addListener("keyboardDidHide", () => setAndroidKbOpen(false)),
        ]);
        if (cancelled) {
          next.forEach((s) => s?.remove?.());
          return;
        }
        subs = next;
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      subs.forEach((s) => s?.remove?.());
      setAndroidKbOpen(false);
    };
  }, [isOpen]);

  // Hardware back closes the sheet (no nested thread screen anymore).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (ev) => {
      ev.detail.register(110, () => close());
    };
    document.addEventListener("ionBackButton", handler);
    return () => document.removeEventListener("ionBackButton", handler);
  }, [isOpen, close]);

  // Deep-link: once comments load, scroll to (and highlight) the focused comment
  // or reply. If it isn't a top-level comment, expand replies so it renders.
  useEffect(() => {
    if (!isOpen || !focusCommentId || !list.hasInitialLoaded) return;
    let attempts = 0;
    let cancelled = false;
    let expandTriggered = false;
    let extraPages = 0;
    // The API already stretches the first page to reach the linked comment, but
    // it stops at 200 — on the two threads longer than that, keep paging.
    const MAX_EXTRA_PAGES = 4;
    const tick = () => {
      if (cancelled) return;
      attempts += 1;
      const el = document.getElementById(`comment-${focusCommentId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        try {
          const prev = el.style.backgroundColor;
          el.style.transition = "background-color 0.3s";
          el.style.backgroundColor = "rgba(101, 177, 101, 0.18)";
          el.style.borderRadius = "12px";
          setTimeout(() => {
            el.style.backgroundColor = prev;
          }, 1600);
        } catch {}
        dispatch(clearCommentFocus());
        return;
      }
      // Not a top-level comment → expand replies so a linked reply renders.
      if (attempts >= 3 && !expandTriggered) {
        expandTriggered = true;
        setExpandForFocus(true);
      }

      // Still nothing: the comment is further down than the first page reached.
      // Pull in another page every ~2.4s, a few times, before giving up.
      if (
        attempts >= 8 &&
        attempts % 12 === 8 &&
        extraPages < MAX_EXTRA_PAGES &&
        !listRef.current?.isInfiniteDisabled
      ) {
        extraPages += 1;
        listRef.current?.loadMore();
      }

      if (attempts < 60) setTimeout(tick, 200);
      else dispatch(clearCommentFocus());
    };
    const t = setTimeout(tick, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isOpen, focusCommentId, list.hasInitialLoaded, dispatch]);

  const [hasAttachment, setHasAttachment] = useState(false);
  const headerCount = list.count ?? postGroup?.comments_count ?? 0;
  const composerKey = replyTarget
    ? `reply-${replyTarget.elementId}`
    : `post-${postId}`;

  // Adaptive height: short for a few comments, growing toward almost-full for
  // many. Fixed height (not a sheet breakpoint) keeps the composer footer
  // pinned and visible; the drag handle below dismisses on a downward swipe.
  // An attached photo adds roughly 200px to the footer (the capped preview plus
  // the rotate/remove row), and the sheet does not grow to meet it: on a post
  // with few comments the 45% floor left the footer taller than the sheet
  // itself, so the send button sat below the bottom edge and the member could
  // pick an image but never post it. Measured on a 360x780 screen: sheet 351px,
  // footer 355px, send button at y=800. Give the preview its own room instead.
  const attachmentFloorPct = hasAttachment ? 68 : 45;
  const sheetHeightPct = Math.min(
    92,
    Math.max(attachmentFloorPct, 38 + headerCount * 11),
  );
  // Android with the keyboard up: fill the natively-resized viewport so the
  // comment list isn't squished. iOS keeps the adaptive peek height + its lift.
  const effectiveHeightPct = androidKbOpen ? 100 : sheetHeightPct;

  // Drag-to-dismiss: translate the modal content with the finger; release past
  // a threshold closes the sheet (the meal-plan food info modal feel).
  const setDrag = (px, animate) => {
    const el = modalRef.current;
    if (!el) return;
    el.style.setProperty("--cs-drag", `${px}px`);
    el.style.setProperty("--cs-drag-anim", animate ? "0.22s" : "0s");
  };
  const onHandleDown = (e) => {
    dragRef.current = { active: true, startY: e.clientY, dy: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(0, false);
  };
  const onHandleMove = (e) => {
    if (!dragRef.current.active) return;
    const dy = Math.max(0, e.clientY - dragRef.current.startY);
    dragRef.current.dy = dy;
    setDrag(dy, false);
  };
  const onHandleUp = () => {
    if (!dragRef.current.active) return;
    const { dy } = dragRef.current;
    dragRef.current.active = false;
    if (dy > 90) {
      setDrag(0, false);
      close();
    } else {
      setDrag(0, true);
    }
  };

  const ensureComposerVisible = useCallback(() => {
    window.setTimeout(() => {
      contentRef.current?.scrollByPoint?.(0, 120, 180);
    }, 260);
  }, []);

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={close}
      backdropDismiss={true}
      className={[
        "comments-sheet-modal",
        keyboardOffset > 0 ? "comments-sheet-modal--keyboard" : "",
      ].join(" ")}
      style={{
        "--height": `${effectiveHeightPct}%`,
        "--cs-kb-offset": `${keyboardOffset}px`,
      }}
    >
      <IonHeader className="comments-sheet-header ion-no-border">
        <div
          className="comments-sheet-handle-wrap"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
        >
          <div className="comments-sheet-handle" />
        </div>
        <div className="comments-sheet-headbar">
          <button
            type="button"
            className="comments-sheet-back"
            aria-label="Chiudi"
            onClick={close}
          >
            <IonIcon icon={chevronBack} />
          </button>
          <div className="comments-sheet-title">Commenti</div>
        </div>
      </IonHeader>

      <IonContent ref={contentRef} className="comments-sheet-content">
        {postGroup && (
          <SheetCommentList
            comments={list.comments}
            hasInitialLoaded={list.hasInitialLoaded}
            error={list.error}
            isInfiniteDisabled={list.isInfiniteDisabled}
            loadMore={list.loadMore}
            onReplyTo={commentsEnabled ? handleReplyTo : undefined}
            lastReply={lastReply}
            focusCommentId={focusCommentId}
            expandForFocus={expandForFocus}
            expandForFocusId={list.anchorCommentId}
          />
        )}
      </IonContent>

      <IonFooter
        ref={footerRef}
        className="comments-sheet-footer ion-no-border"
        onFocusCapture={ensureComposerVisible}
      >
        {replyTarget && (
          <div className="sheet-reply-banner">
            <IonIcon
              className="sheet-reply-banner__icon"
              icon={returnDownForward}
            />
            <span className="sheet-reply-banner__text">
              Rispondi a <strong>{replyTarget.name}</strong>
            </span>
            <button
              type="button"
              className="sheet-reply-banner__cancel"
              aria-label="Annulla risposta"
              onClick={() => setReplyTarget(null)}
            >
              <IonIcon icon={closeIcon} />
            </button>
          </div>
        )}
        {postGroup && commentsEnabled &&
          (replyTarget ? (
            <InsertComment
              key={composerKey}
              variant="sheet"
              element_id={replyTarget.elementId}
              element_type="comment"
              replyToName={replyTarget.replyToName}
              contentRef={contentRef}
              onReplyAdded={handleReplyAdded}
              onAttachmentChange={setHasAttachment}
            />
          ) : (
            <InsertComment
              key={composerKey}
              variant="sheet"
              element_id={postId}
              element_type="post"
              contentRef={contentRef}
              setComments={list.setComments}
              onAttachmentChange={setHasAttachment}
            />
          ))}
      </IonFooter>
    </IonModal>
  );
};

export default CommentsSheet;
